// Name parsing helpers (ported from the desktop Client Track VB codebase's
// modDatabaseClientFunctions.parseSingleNameString/parseNameForContact/parsePrefixFromNameString).
// Shared by the Clients import wizard (src/app/clients/import/import.page.ts) and the Companies
// import wizard (src/app/companies/import/import.page.ts) so a Display Name / Default Contact
// column gets the same First/Middle/Last Name splitting and joint-name ("John and Jane Smith")
// detection in both places.

export interface ParsedName {
  namePrefix: string;
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
}

export interface ParsedNamePair {
  primary: ParsedName;
  spouse: ParsedName | null;
}

// Order matters here exactly as in the VB source: "M." is checked before the longer titles
// (MR, MRS, etc.), so a bare middle-initial-with-period like "M. Smith" is treated as a prefix
// rather than a first initial - preserved as-is to match the original's exact behavior.
const NAME_PREFIX_LIST: string[] = [
  'M.', 'MR', 'MR.', 'MRS', 'MRS.', 'MS', 'MS.', 'MISS', 'MISS.',
  'MLLE', 'MLLE.', 'MME', 'MME.', 'SIR', 'REV', 'REV.', 'DR', 'DR.'
];

// Particles that fold into a multi-word last name (e.g. "Van Der Berg", "St. Pierre") rather
// than being treated as a first or middle name.
const LAST_NAME_PARTICLES: string[] = ['MC', 'MAC', 'VAN', 'DE', 'LE', 'LA', 'DI', 'DEL', 'ST', 'ST.'];

// Finds the first known name prefix anywhere in the string, followed by a space, and returns it
// exactly as it appears in the source (original casing preserved) - mirrors VB's
// parsePrefixFromNameString, including scanning the whole string rather than just the start.
function parsePrefixFromNameString(nameStr: string): string {
  const upper = nameStr.toUpperCase();
  for (const prefix of NAME_PREFIX_LIST) {
    const testStr = prefix + ' ';
    const position = upper.indexOf(testStr);
    if (position !== -1) {
      return nameStr.substr(position, testStr.length);
    }
  }
  return '';
}

// Ports VB's parseSingleNameString: splits a single "display name" string into name prefix,
// first/middle/last name. Handles "Smith, John" -> "John Smith" reordering and multi-word last
// names with particles like "Van Der Berg" or "St. Pierre".
export function parseNameFromDisplayName(nameStrIn: string): ParsedName {
  const result: ParsedName = { namePrefix: '', firstName: '', middleName: '', lastName: '', displayName: '' };
  if (!nameStrIn || !nameStrIn.trim()) return result;

  let nameStr = nameStrIn;

  // Parse prefix from name string if it exists
  const prefix = parsePrefixFromNameString(nameStr);
  if (prefix) {
    result.namePrefix = prefix.trim();
    nameStr = nameStr.replace(prefix, '');
  }

  // First, check for a name in the format 'Smith, John'
  const commaPos = nameStr.indexOf(',');
  if (commaPos !== -1) {
    // change 'Smith, John' to 'John Smith'
    const before = nameStr.substring(0, commaPos).trim();
    const after = nameStr.substring(commaPos + 1).trim();
    nameStr = `${after} ${before}`;
  }

  // Set display name
  result.displayName = nameStr.trim();

  // Parse the name into up to four space-separated sections (the fourth absorbs any remainder,
  // so a name like "John Van Der Berg Jr" still ends up with string4 = "Berg Jr").
  let string1 = '', string2 = '', string3 = '', string4 = '';
  const firstSpace = nameStr.indexOf(' ');
  if (firstSpace !== -1) {
    string1 = nameStr.substring(0, firstSpace);
    let rest = nameStr.substring(firstSpace + 1);
    const secondSpace = rest.indexOf(' ');
    if (secondSpace !== -1) {
      string2 = rest.substring(0, secondSpace);
      rest = rest.substring(secondSpace + 1);
      const thirdSpace = rest.indexOf(' ');
      if (thirdSpace !== -1) {
        string3 = rest.substring(0, thirdSpace);
        string4 = rest.substring(thirdSpace + 1);
      } else {
        string3 = rest;
      }
    } else {
      string2 = rest;
    }
  } else {
    string1 = nameStr;
  }

  string1 = string1.trim();
  string2 = string2.trim();
  string3 = string3.trim();
  string4 = string4.trim();

  const isParticle = (s: string) => LAST_NAME_PARTICLES.includes(s.toUpperCase());

  if (!string2 && !string3 && !string4) {
    // Single word left: if a prefix was found (e.g. "Dr. Smith"), it's a last name only;
    // otherwise it's a first name only.
    if (result.namePrefix) {
      result.lastName = string1;
    } else {
      result.firstName = string1;
    }
  } else if (!string3 && !string4) {
    if (isParticle(string1)) {
      result.lastName = `${string1} ${string2}`;
    } else {
      result.firstName = string1;
      result.lastName = string2;
    }
  } else if (!string4) {
    if (isParticle(string2)) {
      result.firstName = string1;
      result.lastName = `${string2} ${string3}`;
    } else {
      result.firstName = string1;
      result.middleName = string2;
      result.lastName = string3;
    }
  } else {
    if (isParticle(string2)) {
      result.firstName = string1;
      result.lastName = `${string2} ${string3} ${string4}`;
    } else {
      result.firstName = string1;
      result.middleName = string2;
      result.lastName = `${string3} ${string4}`;
    }
  }

  return result;
}

// Finds the first joint-name joiner in a string (order mirrors the VB joiner list - AND, &, ET -
// with '+' added on top per explicit request). Word-based joiners ('and', 'et') require
// surrounding whitespace so they don't false-match inside a name (e.g. "Andrea", "Etta"); symbol
// joiners ('&', '+') don't require spaces since they're unlikely to appear as literal characters
// in a person's name.
const NAME_JOINER_PATTERNS: RegExp[] = [
  /\s+and\s+/i,
  /\s*&\s*/,
  /\s+et\s+/i,
  /\s*\+\s*/,
];

function findNameJoiner(str: string): { index: number; length: number } | null {
  for (const pattern of NAME_JOINER_PATTERNS) {
    const match = pattern.exec(str);
    if (match) return { index: match.index, length: match[0].length };
  }
  return null;
}

// Splits tempName on the given joiner match into two independently-parsed names. If the first
// half is only a single word (e.g. "David" in "David and Fany Merritt"), it has no last name of
// its own - borrow the last word of the second half, same as VB's "look for 'John Smith & Karen
// Jones' by looking for a space in the first half" step.
function splitTwoNames(tempName: string, joinerMatch: { index: number; length: number }): ParsedNamePair {
  let firstHalf = tempName.substring(0, joinerMatch.index).trim();
  const secondHalf = tempName.substring(joinerMatch.index + joinerMatch.length).trim();

  if (firstHalf && !firstHalf.includes(' ')) {
    const words = secondHalf.split(/\s+/).filter(w => w);
    const borrowedLastName = words.length > 1 ? words[words.length - 1] : '';
    if (borrowedLastName) firstHalf = `${firstHalf} ${borrowedLastName}`;
  }

  return {
    primary: parseNameFromDisplayName(firstHalf),
    spouse: secondHalf ? parseNameFromDisplayName(secondHalf) : null
  };
}

// Ports the "two names" half of VB's parseNameForContact: detects a joint display name like
// "David and Fany Merritt", "Merritt, David and Fany", or "Merritt, David and Diaz, Fany" and
// splits it into a primary + spouse name pair. Returns spouse: null when no joiner is found (the
// ordinary single-person case), in which case only `primary` is meaningful.
export function parseNamesFromDisplayName(nameStrIn: string): ParsedNamePair {
  if (!nameStrIn || !nameStrIn.trim()) {
    return { primary: parseNameFromDisplayName(''), spouse: null };
  }

  let tempName = nameStrIn.trim();

  const joinerMatch = findNameJoiner(tempName);
  if (!joinerMatch) {
    return { primary: parseNameFromDisplayName(tempName), spouse: null };
  }

  const beforeJoiner = tempName.substring(0, joinerMatch.index).trim();
  const afterJoiner = tempName.substring(joinerMatch.index + joinerMatch.length).trim();

  // "Merritt, David and Diaz, Fany" - each half is already a fully self-contained "Last, First"
  // name (parseNameFromDisplayName handles the comma itself) - parse them independently rather
  // than trying to share one last name between them, which is what a literal VB port would do
  // (VB only ever looks for the first comma in the whole string, so it mishandles this case).
  if (beforeJoiner.includes(',') && afterJoiner.includes(',')) {
    return {
      primary: parseNameFromDisplayName(beforeJoiner),
      spouse: parseNameFromDisplayName(afterJoiner)
    };
  }

  // "Merritt, David and Fany" - a single comma before the joiner means everything before it is a
  // shared last name for both people; move it to the very end of the whole string (same as VB)
  // before splitting on the joiner, so the second half already ends up with it attached.
  const commaPos = tempName.indexOf(',');
  if (commaPos !== -1 && commaPos < joinerMatch.index) {
    const sharedLastName = tempName.substring(0, commaPos).trim();
    tempName = `${tempName.substring(commaPos + 1).trim()} ${sharedLastName}`.trim();
    const rematch = findNameJoiner(tempName);
    if (rematch) return splitTwoNames(tempName, rematch);
  }

  return splitTwoNames(tempName, joinerMatch);
}
